import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Wallet,
  TrendingUp,
  Coins,
  DollarSign,
  Users,
  Crown,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Clock,
  Send,
  Smartphone,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  Download,
  Filter,
  BarChart3,
  Activity,
  Percent,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const RANGE_KEYS = ["today","yesterday","7d","30d","month","last_month","custom"];
const RANGE_LABEL_MAP = { today: "range_today", yesterday: "range_yesterday", "7d": "range_7d", "30d": "range_30d", month: "range_this_month", last_month: "range_last_month", custom: "range_custom" };

const CHART_COLORS = ["#0F62FE", "#2BA8F0", "#7CB342", "#F39C12", "#9C27B0", "#EC4899", "#10B981", "#F97316", "#6366F1", "#14B8A6"];

const fmtUSD = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(n || 0));
const fmtInt = (n) => Number(n || 0).toLocaleString("en-US");
const fmtCcy = (n, ccy) => `${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })} ${ccy || ""}`;

function KpiCard({ icon: Icon, label, value, sub, tone = "blue", testid }) {
  const tones = {
    blue: "bg-[#2BA8F0]",
    slate: "bg-[#8A99A8]",
    green: "bg-[#7CB342]",
    purple: "bg-[#9C27B0]",
    amber: "bg-[#F39C12]",
    rose: "bg-rose-500",
    indigo: "bg-indigo-500",
  };
  return (
    <div
      data-testid={testid}
      className={`${tones[tone] || tones.blue} rounded-md p-4 min-h-[120px] flex items-center gap-3 transition-opacity hover:opacity-95`}
    >
      <Icon className="w-10 h-10 text-white/95 flex-shrink-0" strokeWidth={1.8} />
      <div className="ml-auto text-right min-w-0">
        <div
          className="text-xl sm:text-2xl font-bold text-white leading-tight break-all"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          {value}
        </div>
        <div className="text-[11px] text-white/95 font-medium mt-0.5 uppercase tracking-wider">{label}</div>
        {sub && <div className="text-[10px] text-white/80 mt-0.5 truncate max-w-[220px] ml-auto">{sub}</div>}
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, testid, right }) {
  return (
    <div className="bg-white rounded-md border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-sm font-bold text-slate-900">{title}</div>
          {subtitle && <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
        {right}
      </div>
      <div className="w-full">{children}</div>
    </div>
  );
}

function ProgressRow({ label, value, max, sub, tone = "blue" }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const barTones = {
    blue: "bg-[#0F62FE]",
    green: "bg-[#7CB342]",
    amber: "bg-[#F39C12]",
    purple: "bg-[#9C27B0]",
  };
  return (
    <div className="mb-2">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-semibold text-slate-800">{label}</span>
        <span className="text-slate-600 font-mono">{sub}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded overflow-hidden">
        <div className={`h-full ${barTones[tone] || barTones.blue}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function AnalyticsDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const role = user?.role;
  const isAdmin = role === "MASTER_ADMIN";
  const isMaster = role === "MASTER_AGENT";
  const isAgent = role === "AGENT";
  const isVerifier = role === "CASH_VERIFIER";
  const canExport = isAdmin || isMaster;

  const [range, setRange] = useState("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState("");
  const [urlParams] = useSearchParams();
  // ?scope=agents ⇒ restrict analytics to AGENT-submitted topups only.
  const roleScope = urlParams.get("scope") === "agents" ? "AGENT"
    : urlParams.get("scope") === "masters" ? "MASTER_AGENT" : null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ range });
      if (range === "custom" && customFrom && customTo) {
        params.set("date_from", customFrom);
        params.set("date_to", customTo);
      }
      if (roleScope) params.set("role_scope", roleScope);
      const { data } = await api.get(`/analytics?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [range, customFrom, customTo, roleScope]);

  // Re-run on range / roleScope changes only (custom-date apply is a manual
  // button that calls `load` directly — this avoids firing while the user is
  // still typing the "from"/"to" dates).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [range, roleScope]);

  const runExport = async (format) => {
    setExporting(format);
    try {
      const params = new URLSearchParams({ range, format });
      if (range === "custom" && customFrom && customTo) {
        params.set("date_from", customFrom);
        params.set("date_to", customTo);
      }
      const res = await api.get(`/analytics/export?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `analytics_${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${format.toUpperCase()} report`);
    } catch (e) {
      toast.error(t("analytics.export_failed"));
    } finally { setExporting(""); }
  };

  const kpis = data?.kpis || {};
  const financials = data?.financials || {};
  const charts = data?.charts || {};
  const verifier = data?.verifier || {};

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header + Filters */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5">
            <BarChart3 className="w-3.5 h-3.5 text-slate-500" />
            {t("analytics.title")}
          </div>
          <h1
            className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {t("analytics.title")}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {t("analytics.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canExport && (
            <div className="flex items-center gap-1 border border-slate-300 rounded-lg p-1 bg-white" data-testid="analytics-export">
              {["csv", "xlsx", "pdf"].map((fmt) => (
                <button
                  key={fmt}
                  onClick={() => runExport(fmt)}
                  disabled={!!exporting || loading}
                  data-testid={`analytics-export-${fmt}`}
                  className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-[11px] font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {exporting === fmt ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} {fmt}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Range picker */}
      <div className="bg-white rounded-md border border-slate-200 p-3 flex flex-wrap items-center gap-2">
        {RANGE_KEYS.map((k) => (
          <button
            key={k}
            onClick={() => setRange(k)}
            data-testid={`range-${k}`}
            className={`h-9 px-3 rounded-md text-xs font-semibold border transition-colors ${
              range === k
                ? "bg-[#0F62FE] text-white border-[#0F62FE]"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {t(`ui.${RANGE_LABEL_MAP[k]}`)}
          </button>
        ))}
        {range === "custom" && (
          <div className="flex items-center gap-2 ml-2">
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="h-9 rounded-md border border-slate-300 px-3 text-xs"
              data-testid="range-from"
            />
            <span className="text-slate-400 text-xs">→</span>
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="h-9 rounded-md border border-slate-300 px-3 text-xs"
              data-testid="range-to"
            />
            <button
              onClick={load}
              disabled={!customFrom || !customTo || loading}
              className="h-9 px-3 rounded-md bg-[#0F62FE] text-white text-xs font-semibold disabled:opacity-40"
              data-testid="range-apply"
            >
              Apply
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div className="bg-white rounded-md border border-slate-200 py-12 text-center text-slate-500 text-sm">
          <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> {t("analytics.crunching")}
        </div>
      )}

      {!loading && isVerifier && <VerifierView v={verifier} />}

      {!loading && !isVerifier && data && (
        <>
          {/* KPI CARDS */}
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="kpi-grid">
            <KpiCard testid="kpi-revenue" icon={DollarSign} tone="blue"   label={t("analytics.kpi_total_revenue")} value={fmtUSD(kpis.total_revenue_usd)} />
            <KpiCard testid="kpi-topups"  icon={Send}       tone="green"  label={t("analytics.kpi_send_topups")}   value={fmtInt(kpis.total_topups)} />
            <KpiCard testid="kpi-cash"    icon={Smartphone} tone="purple" label={t("analytics.kpi_send_cash")}     value={fmtInt(kpis.total_send_cash)} />
            <KpiCard testid="kpi-funding" icon={Wallet}     tone="amber"  label={t("analytics.kpi_wallet_funded")} value={fmtUSD(kpis.total_wallet_funding_usd)} />
            <KpiCard testid="kpi-fees"    icon={Percent}    tone="indigo" label={t("analytics.kpi_conv_fees")}    value={fmtUSD(kpis.total_convenience_fees)} sub={t("analytics.agent_profit_margin")} />
            {isAdmin && <KpiCard testid="kpi-agents"    icon={Users}       tone="slate"  label={t("analytics.kpi_total_agents")}      value={fmtInt(kpis.total_agents)} />}
            {isAdmin && <KpiCard testid="kpi-masters"   icon={Crown}       tone="purple" label={t("analytics.kpi_master_agents")}     value={fmtInt(kpis.total_master_agents)} />}
            {isAdmin && <KpiCard testid="kpi-active"    icon={ShieldCheck} tone="green"  label={t("analytics.kpi_active_users")}      value={fmtInt(kpis.total_active_users)} sub={`${kpis.total_inactive_users} ${t("analytics.inactive")}`} />}
            <KpiCard testid="kpi-pending"   icon={Clock}       tone="amber"  label={t("analytics.kpi_pending_cash")}      value={fmtInt(kpis.total_pending_cash)} />
            <KpiCard testid="kpi-completed" icon={CheckCircle2}tone="green"  label={t("analytics.kpi_completed")}         value={fmtInt(kpis.total_completed)} />
            <KpiCard testid="kpi-failed"    icon={XCircle}     tone="rose"   label={t("analytics.kpi_failed")}            value={fmtInt(kpis.total_failed)} />
          </div>

          {/* FINANCIAL STATS */}
          <div className="bg-white rounded-md border border-slate-200 p-5" data-testid="financials">
            <div className="text-sm font-bold text-slate-900 mb-3 inline-flex items-center gap-1.5">
              <Coins className="w-4 h-4 text-[#0F62FE]" /> {t("analytics.financial_stats")}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
              <FinancialTile label={t("analytics.total_profit")} value={fmtUSD(financials.total_profit_usd)} tone="emerald" />
              <FinancialTile label={t("analytics.total_commission")} value={fmtUSD(financials.total_commission_usd)} tone="blue" />
              <FinancialTile label={t("analytics.convenience_fees")} value={fmtUSD(financials.total_convenience_fees)} tone="amber" />
              <FinancialTile label={t("analytics.topup_wallet_balance")} value={fmtUSD(financials.total_wallet_balance_usd)} tone="slate" />
              <FinancialTile label={t("analytics.money_sent_topup")} value={fmtUSD(financials.total_money_sent_topup_usd)} tone="blue" />
              <FinancialTile label={t("analytics.money_sent_cash")} value={fmtInt(financials.total_money_sent_cash)} tone="slate" />
              <FinancialTile
                label={t("analytics.cash_balances")}
                value={(financials.total_cash_balances || []).length
                  ? (financials.total_cash_balances || []).map((c) => `${fmtInt(c.total)} ${c.currency}`).join(" · ")
                  : "—"}
                tone="purple"
                small
              />
            </div>
          </div>

          {/* Charts row 1 — Service split + Daily revenue */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ChartCard title={t("analytics.chart_sales_by_service")} subtitle={t("analytics.chart_topup_vs_cash")} testid="chart-service">
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={charts.sales_by_service || []}
                    dataKey="sales_usd"
                    nameKey="service"
                    cx="50%"
                    cy="50%"
                    outerRadius={82}
                    label={(e) => `${e.service}: ${fmtUSD(e.sales_usd)}`}
                  >
                    {(charts.sales_by_service || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtUSD(v)} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <div className="lg:col-span-2">
              <ChartCard title={t("analytics.chart_daily_revenue")} subtitle={t("analytics.chart_daily_sub")} testid="chart-daily">
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={charts.daily_revenue || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => fmtUSD(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="topup_usd" stroke="#0F62FE" strokeWidth={2} name={t("analytics.legend_send_topup")} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="cash_usd" stroke="#9C27B0" strokeWidth={2} name={t("analytics.legend_send_cash")} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="total_usd" stroke="#7CB342" strokeWidth={2} strokeDasharray="4 4" name={t("analytics.legend_total")} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          </div>

          {/* Charts row 2 — Countries + Providers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title={t("analytics.chart_top_countries")} subtitle={t("analytics.chart_top_countries_sub")} testid="chart-country">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={(charts.sales_by_country || []).slice(0, 10)} layout="vertical" margin={{ left: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="country_code" type="category" tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => fmtUSD(v)} />
                  <Bar dataKey="sales_usd" fill="#0F62FE" name={t("analytics.legend_sales_usd")} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title={t("analytics.chart_sales_by_provider")} subtitle={t("analytics.chart_sales_by_provider_sub")} testid="chart-provider">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={(charts.sales_by_provider || []).slice(0, 8)}
                    dataKey="sales_usd"
                    nameKey="provider"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={(e) => e.provider}
                  >
                    {(charts.sales_by_provider || []).slice(0, 8).map((_, i) => <Cell key={i} fill={CHART_COLORS[i]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtUSD(v)} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Charts row 3 — Currencies + Funding methods */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title={t("analytics.chart_cash_by_ccy")} subtitle={t("analytics.chart_cash_by_ccy_sub")} testid="chart-currency">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={charts.sales_by_currency || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="currency" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="sent" fill="#0F62FE" name={t("analytics.legend_sent_base")} />
                  <Bar dataKey="received_htg" fill="#9C27B0" name={t("analytics.legend_received_htg")} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {(charts.funding_by_method || []).length > 0 && (
              <ChartCard title={t("analytics.chart_funding_by_method")} subtitle={t("analytics.chart_funding_sub")} testid="chart-funding">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={charts.funding_by_method}
                      dataKey="total_usd"
                      nameKey="method"
                      cx="50%"
                      cy="50%"
                      outerRadius={95}
                      label={(e) => `${e.method}: ${fmtUSD(e.total_usd)}`}
                    >
                      {(charts.funding_by_method || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtUSD(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
          </div>

          {/* Charts row 4 — Top agents / Top master agents */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title={t("analytics.chart_top_10_agents")} subtitle={t("analytics.chart_top_agents_sub")} testid="chart-top-agents">
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={charts.top_agents || []} layout="vertical" margin={{ left: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="username" type="category" tick={{ fontSize: 10 }} width={100} />
                  <Tooltip formatter={(v) => fmtUSD(v)} />
                  <Bar dataKey="sales_usd" fill="#7CB342" name={t("analytics.legend_sales_usd")} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {isAdmin && (charts.top_master_agents || []).length > 0 && (
              <ChartCard title={t("analytics.chart_top_10_masters")} subtitle={t("analytics.chart_top_masters_sub")} testid="chart-top-masters">
                <ResponsiveContainer width="100%" height={340}>
                  <BarChart data={charts.top_master_agents || []} layout="vertical" margin={{ left: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis dataKey="username" type="category" tick={{ fontSize: 10 }} width={100} />
                    <Tooltip formatter={(v) => fmtUSD(v)} />
                    <Bar dataKey="sales_usd" fill="#9C27B0" name={t("analytics.legend_sales_usd")} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function FinancialTile({ label, value, tone = "blue", small }) {
  const tones = {
    blue: "text-[#0F62FE]",
    emerald: "text-emerald-600",
    amber: "text-amber-600",
    purple: "text-violet-700",
    slate: "text-slate-800",
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`${small ? "text-sm" : "text-lg"} font-bold ${tones[tone] || tones.blue} mt-0.5 break-all`}>{value}</div>
    </div>
  );
}

function VerifierView({ v }) {
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard testid="verifier-pending" icon={Clock} tone="amber" label="Pending" value={fmtInt(v.pending_requests)} />
        <KpiCard testid="verifier-confirmed" icon={CheckCircle2} tone="green" label="Confirmed" value={fmtInt(v.confirmed_requests)} />
        <KpiCard testid="verifier-rejected" icon={XCircle} tone="rose" label="Rejected" value={fmtInt(v.rejected_requests)} />
        <KpiCard testid="verifier-today" icon={Activity} tone="blue" label="Completed today" value={fmtInt(v.completed_today)} />
        <KpiCard testid="verifier-wallet" icon={Wallet} tone="purple" label="Wallet balance" value={fmtCcy(v.wallet_balance, v.wallet_currency)} />
        <KpiCard testid="verifier-commission" icon={TrendingUp} tone="green" label="Commission earned" value={fmtCcy(v.total_commission, v.wallet_currency)} />
      </div>
      <div className="bg-white rounded-md border border-slate-200 p-5">
        <div className="text-sm font-bold text-slate-900 mb-3 inline-flex items-center gap-1.5">
          <Activity className="w-4 h-4 text-[#0F62FE]" /> Daily activity
        </div>
        <ProgressRow label="Confirmed" value={v.confirmed_requests} max={Math.max(1, v.confirmed_requests + v.rejected_requests + v.pending_requests)} sub={`${v.confirmed_requests}`} tone="green" />
        <ProgressRow label="Rejected" value={v.rejected_requests} max={Math.max(1, v.confirmed_requests + v.rejected_requests + v.pending_requests)} sub={`${v.rejected_requests}`} tone="amber" />
        <ProgressRow label="Pending" value={v.pending_requests} max={Math.max(1, v.confirmed_requests + v.rejected_requests + v.pending_requests)} sub={`${v.pending_requests}`} tone="blue" />
      </div>
    </>
  );
}
